import type {
    LomdaSceneAvatar,
    MediaShadowPreset,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";

/**
 * A free-form element's geometry on the scene canvas. All values are
 * percentages of the fixed 1920×1080 design canvas, so they're resolution
 * independent and render identically at any `--scene-font-scale`.
 *
 * `x`/`y` are the TOP-LEFT corner (not center) — keeps resize-from-corner math
 * trivial. `rotation` is in degrees, applied around the box center.
 */
export interface ElementTransform {
    /** Top-left X, % of canvas width. */
    x: number;
    /** Top-left Y, % of canvas height. */
    y: number;
    /** Width, % of canvas width. */
    width: number;
    /** Height, % of canvas height. */
    height: number;
    /** Rotation in degrees, clockwise, around the box center. */
    rotation: number;
}

export type SceneElementKind = "text" | "media" | "avatar";

/** Vertical placement of a text element's content inside its box. */
export type VerticalAlign = "top" | "center" | "bottom";

interface SceneElementBase {
    id: string;
    kind: SceneElementKind;
    transform: ElementTransform;
    /** Stacking order within the scene; higher renders on top. */
    zIndex: number;
    /** When true the element can't be moved/resized/selected on the canvas. */
    locked?: boolean;
}

/** The rich-text editor element. Holds the Tiptap HTML (incl. custom blocks). */
export interface TextElement extends SceneElementBase {
    kind: "text";
    html: string;
    /**
     * Vertical placement of the content inside the element box. "center"
     * mirrors the original template-A `justify-center`; defaults to "top".
     */
    verticalAlign?: VerticalAlign;
}

export interface MediaElementData {
    type: "image" | "video";
    url: string;
    objectFit: "contain" | "cover";
    /** Corner radius in unscaled (1920-design) px. */
    borderRadius: number;
    /** Drop-shadow preset for the media box. Default "none". */
    shadow?: MediaShadowPreset;
    /**
     * Current tint applied to an SVG image. Only meaningful when `url` points at
     * an SVG; `url` already holds the recolored markup, this just drives the
     * toolbar swatch.
     */
    svgColor?: string;
}

/** An image or video element. A scene may have any number of these. */
export interface MediaElement extends SceneElementBase {
    kind: "media";
    media: MediaElementData;
}

/**
 * The HeyGen avatar element. Positioning lives on the element (transform/zIndex);
 * `avatar` keeps the HeyGen config + visual styling (ring/shadow/radius/opacity).
 */
export interface AvatarElement extends SceneElementBase {
    kind: "avatar";
    avatar: LomdaSceneAvatar;
}

export type SceneElement = TextElement | MediaElement | AvatarElement;

export function isTextElement(el: SceneElement): el is TextElement {
    return el.kind === "text";
}
export function isMediaElement(el: SceneElement): el is MediaElement {
    return el.kind === "media";
}
export function isAvatarElement(el: SceneElement): el is AvatarElement {
    return el.kind === "avatar";
}
